import { createApp, nextTick } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store';
import { createI18n } from 'vue-i18n';
import { getCurrentProjectUser } from './services/currentUser';
import { loadAuthControl, isLoginRemoved } from './services/authControl';
import { identifyCatalystUser } from './weblytics';
import './assets/css/zoho-loader.css';

// The loader lives in public/index.html so it can paint before this bundle
// even downloads. It is held open rather than toggled — see the comment there.
// No-ops if that markup is missing.
const loader = {
  acquire: () => window.__appLoader && window.__appLoader.acquire(),
  release: () => window.__appLoader && window.__appLoader.release(),
  booted: () => window.__appLoader && window.__appLoader.booted(),
};

// const authenticateUser = async (router, app) => {
//   try {
//     const userManagement = window.catalyst.userManagement;
//     const currentUser = await userManagement.getCurrentProjectUser();

//     // console.log('Authenticated:', currentUser.content);

//     app.config.globalProperties.$currentUser = currentUser.content;

//     // return true;
//   } catch (e) {
//     console.error('Authentication Error:', e);
//     router.push('/login');
//     // return false;
//   }
// };

// F5: `userPromise` is started by the caller BEFORE the router is ready, so the
// request is already in flight while the router resolves rather than queued
// behind it. Both branches below await the same promise — the shared service
// guarantees one request no matter how many callers there are.
const authenticateUser = async (router, app, userPromise) => {
  const currentPath = router.currentRoute.value.path;

  // AUTH_CONTROL kill switch. When { ControlName: 'RemoveLogin', IsActive: true }
  // is present the whole gate below is skipped: nobody is bounced to /login,
  // and a visitor who landed on /login is sent to the home page instead of
  // being shown a sign-in form they are not required to use.
  //
  // The user lookup is still awaited when it happens to succeed, so a signed-in
  // visitor keeps their name in the header while the switch is on. It just no
  // longer decides whether the page is allowed to render.
  if (isLoginRemoved()) {
    // Deliberately NOT awaited.
    //
    // This branch has already decided the visitor does not need to be signed
    // in, so nothing about the page depends on the answer: $currentUser is
    // only read to show a name. Awaiting it put a full round trip in front of
    // app.mount() for a request that, on a site browsing anonymously, is
    // expected to 401 — measured at 257ms on one load, and the service
    // deliberately does not cache failures, so a later caller pays for it
    // again rather than inheriting a cached rejection.
    //
    // Attaching it on arrival keeps the header's name behaviour identical for
    // a visitor who IS signed in, without holding the page for one who is not.
    userPromise
      .then((currentUser) => {
        app.config.globalProperties.$currentUser = currentUser.content;
      })
      .catch(() => {
        // Anonymous browsing is the point while this switch is on.
      });

    if (currentPath === '/login' || currentPath === '/training/login') {
      await router.replace('/');
    }
    return;
  }

  if (currentPath.startsWith('/training/validate/') || currentPath.startsWith('/training/validate')) {
    try {

      const currentUser = await userPromise;

      app.config.globalProperties.$currentUser = currentUser.content;

    } catch (e) {
      console.error('Authentication Error:', e);
      // if (currentPath.startsWith('/training/validate/') || currentPath.startsWith('/training/validate')) {
      //   return;
      // }
      router.push('/training/login');
    }
  } else {
    try {

      const currentUser = await userPromise;

      app.config.globalProperties.$currentUser = currentUser.content;

    } catch (e) {
      console.error('Authentication Error:', e);
      if (currentPath.startsWith('/training/login/') || currentPath.startsWith('/training/login')) {
        return;
      }
      router.push('/login');
    }
  }
  // alert(currentPath)
};

const initApp = async () => {

  const app = createApp(App);
  app.use(router)

  // F5: fire the user lookup before awaiting the router, so the request and
  // route resolution overlap instead of running nose-to-tail. The rejection is
  // parked here because it is handled inside authenticateUser below; without
  // this no-op catch the browser reports an unhandled rejection in the window
  // between starting the request and awaiting it there.
  const userPromise = getCurrentProjectUser();
  userPromise.catch(() => {});

  // Tell Weblytics who this is, as soon as the lookup lands.
  //
  // Hung off the same promise rather than a store watcher: this fires on every
  // route, including ones that never mount the header, and costs no extra
  // request — getCurrentProjectUser() is memoised, so this is the same answer
  // authenticateUser is already waiting on. Deliberately not awaited; analytics
  // must never sit in front of app.mount().
  //
  // The rejection path is the signed-out case, which is normal while the
  // AUTH_CONTROL RemoveLogin switch is on.
  userPromise
    .then((currentUser) => identifyCatalystUser(currentUser && currentUser.content))
    .catch(() => {});

  // Started alongside the user lookup, not after it, so the two requests
  // overlap and the switch costs no extra wall-clock time on boot. It has to be
  // settled before authenticateUser runs, since that is where the redirect
  // decision is made — and before the router guard below, which reads it
  // synchronously.
  const authControlPromise = loadAuthControl();

  await router.isReady();
  await authControlPromise;

  await authenticateUser(router, app, userPromise);
  // const isAuthenticated = await authenticateUser(router, app);
  // if (!isAuthenticated) return;

  const currentRoute = router.currentRoute.value;
  const id = currentRoute.params.ProjectId;

  // console.log('Route param ID:', id);
  // console.log("Route: ", router.currentRoute);
  // console.log("Route: ", router.currentRoute.value);
  // console.log("Route: ", router.currentRoute._rawValue);
  // console.log("Route: ", router.currentRoute._value);

  const getProjectId = currentRoute.params.ProjectId;
  const getCustomerId = currentRoute.params.CustomerId;

  const homeHtmlContent = {
    "en": {
      "header": {
        "header_notification": "Welcome to Zoho MEA Intranet. Discover our solutions and regional customer success stories.",
        "site_navigation": [
          "Home",
          "Projects",
          "Verticals",
          "Methodology",
        ],
        "button_group": [
          {
            "globe": "EN",
            "login": "Login"
          }
        ]
      },
      "mainContent": {
        "hero_head": "Methodology of",
        "text_primary": "Zoho MEA's ",
        "primary_services": "Services",
        "excerpt_mb_4": "From Dubai to Cairo, Riyadh to Cape Town, Zoho's MEA team drives business momentum with focused regional expertise, energetic support, and stellar solutions that equip organizations to overcome challenges and accelerate growth.",
        "cta_primary": "Say Hello! "
      },
      "featureContent": {

      },
      "patnerViewContent": {
        "client_head": "Trusted ",
        "client_head_two": "Organizations"
      },
      "serviceViewContent": {
        "mainHeader": {
          "heading": "Seamless ",
          "highlight": "Implementation Journey",
          "highlight_break": "with MEA Services"
        },
        "service_contents": [
          {
            "title": "Discovery",
            "content": "This phase comprises three core elements—gathering requirements, researching, and solution designing. Our team of analysts will exclusively lead the way to understand your business needs and provide customized solutions to meet your requirements. Our joint-solution design approach involves workshops and sessions to define business and technology requirements",
            "action": "Learn More",
            "icon": "bx bx-bulb"
          },
          {
            "title": "Develop",
            "content": "During this phase, the Business Analysts work closely with the developers to make customizations that are tailor-made to the client's exclusive requirement. The 'iterative and agile-based' approach is followed to reduce runtime errors, bugs, and so. Rigorous internal testing is done before pushing the project to UAT—followed by the final release.",
            "action": "Learn More",
            "icon": "bx bx-code-alt"
          },
          {
            "title": "Deliver",
            "content": "Multiple sub-phases, such as onboarding & training, data migration, hyper-care, and project handover, will transition during this phase. The customer will be given heightened customer support and attention immediately after the onboarding and data migration stages due to the immediate change in operations. Our business analysts proactively work with the customer to address feedbacks for a seamless transition and positive customer experience.",
            "action": "Learn More",
            "icon": "bx bx-box"
          }
        ],
      },
      "slider_contents": {
        "main_header": {
          "header_h2": "Zoho's ",
          "span_one_value": "Unique Value",
          "span_two_value": "Proposition",
        },
        "service_slider_contents": [
          {
            "title": "Comprehensive, Integrated Suite with Native AI",
            "content": "Zoho offers a vast array of business applications, each designed to integrate seamlessly to address diverse needs"
          },
          {
            "title": "Low-Code/No-Code Setup",
            "content": "Thanks to Zoho Creator, users can build versatile apps for various businesses with minimal coding"
          },
          {
            "title": "The 'Privacy First' Approach",
            "content": "A strong emphasis on data security and privacy, extending GDPR protections and global compliances"
          },
          {
            "title": "Affordable, yet Astoundingly Good!",
            "content": "Enterprise-grade features at competitive, transparent prices suitable for all business sizes"
          },
          {
            "title": "User-Friendly Solutions",
            "content": "Intuitive interfaces that minimize learning curves and boost user adoption, thanks to rigorous internal testing"
          },
          {
            "title": "Localized Solutions",
            "content": "Customizable features that respect regional business practices and languages."
          },
          {
            "title": "24/7 Support & Community",
            "content": "Reliable customer support backed by a vibrant user community and extensive resources"
          },
        ]
      },
      "step_view_content": {
        "main_header": {
          "Heading_One": "Built",
          "Heading_Yellow": "to Scale, Designed",
          "Heading_Third": "to Perform"
        },
        "step_content": [
          {
            "title": "#1 Discovery",
            "content": "Expert insights and strategic planning tailored to your business needs by our experts"
          },
          {
            "title": "#2 Wireframing & Prototype",
            "content": "Mapping the solution structure and flow through interactive wireframes and prototypes"
          },
          {
            "title": "#3 Design",
            "content": "Building striking, user-centered designs that echo your brand with great precision"
          },
          {
            "title": "#4 Development",
            "content": "Engineering dynamic, high-impact solutions that grow with you—fueled by modern innovation"
          },
          {
            "title": "#5 UAT & Deployment",
            "content": "Thorough user acceptance testing and flawless deployment after meticulous and rigorous quality checks"
          }
        ],
      },
      "footerViewContent": {
        "getstart_card": {
          "header": "Grow Like a Pro, Powered by Zoho!",
          "paragraph": "Zero in on your needs, grab the perfect Zoho apps, and let us power up your business-tech connection—built just for you!",
          "cta_btn": "Get in Touch! ",
        },
        "footer_logo": {
          "paragraph": "Far far away, behind the word mountains, far from the countries Vokalia and Consonantia, there live the blind texts.",
        },
        "footer_list": [
          {
            "title": "Internal Links",
            "Internal Links": [
              "Sales",
              "Pre Sales",
              "Partners",
              "Marketing",
              "Account",
              "Management",
            ],
          },
          {
            "title": "About Zoho MEA",
            "About Zoho MEA": [
              "General",
              "Communication",
              "Projects"
            ],
          },
          {
            "title": "Contact Sales",
            "Contact Sales": [
              {
                "Phone": [
                  "1800 103 1123",
                  "1800 572 3535"
                ]
              }, {
                "Email": [
                  "sales@zohocorp.com"
                ]
              }
            ],
          },
        ]
      },
    },
    "ar": {
      "header": {
        "header_notification": "خدمات MEA من Zoho تقدم تجربة متطورة وحديثة",
        "site_navigation": [
          "الرئيسية",
          "المشاريع",
          "القطاعات",
          "المنهجية"
        ],
        "button_group": [
          {
            "globe": "العربية",
            "login": "تسجيل الدخول"
          }
        ]
      },
      "mainContent": {
        "hero_head": "المنهجية الخاصة بـ",
        "text_primary": "Zoho MEA",
        "primary_services": "الخدمات",
        "excerpt_mb_4": "رؤى جذابة، تحديثات إقليمية متميزة، وتوجيه استراتيجي فعال - هذا هو فريق MEA من Zoho. من دبي إلى القاهرة، من الرياض إلى كيب تاون، نحن القوة الدافعة لنجاح أعمالك. حلول مصممة باحترافية تحول التحديات إلى إنجازات، بدقة متناهية!",
        "cta_primary": "تواصل معنا!"
      },
      "featureContent": {

      },
      "patnerViewContent": {
        "client_head": "جهات ",
        "client_head_two": "موثوقة"
      },
      "serviceViewContent": {
        "mainHeader": {
          "heading": "رحلة تنفيذ ",
          "highlight": "سلسة",
          "highlight_break": "مع Zoho MEA"
        },
        "service_contents": [
          {
            "title": "الاستكشاف",
            "content": "تتكون هذه المرحلة من ثلاث عناصر أساسية - جمع المتطلبات، البحث، وتصميم الحلول. سيقود فريق المحللين لدينا الطريق لفهم احتياجات عملك وتقديم حلول مخصصة تلبي متطلباتك. يشمل نهجنا المشترك لتصميم الحل ورش عمل وجلسات لتحديد المتطلبات التجارية والتقنية.",
            "action": "اعرف المزيد"
          },
          {
            "title": "التطوير",
            "content": "لوريم إيبسوم هو ببساطة نص شكلي يستخدم في صناعة الطباعة والتنضيد.",
            "action": "اعرف المزيد"
          },
          {
            "title": "التسليم",
            "content": "لوريم إيبسوم هو ببساطة نص شكلي يستخدم في صناعة الطباعة والتنضيد.",
            "action": "اعرف المزيد"
          }
        ]
      },
      "slider_contents": {
        "main_header": {
          "header_h2": "القيمة ",
          "span_one_value": "الفريدة",
          "span_two_value": "من Zoho"
        },
        "service_slider_contents": [
          {
            "title": "دعم ما بعد البيع",
            "content": "دعم عملاء موثوق به مدعوم بمجتمع مستخدمين نشط وموارد شاملة"
          },
          {
            "title": "إعداد بلا كود أو بكود منخفض",
            "content": "بفضل Zoho Creator، يمكن للمستخدمين إنشاء تطبيقات متعددة الاستخدامات بمجهود برمجي بسيط"
          },
          {
            "title": "مجموعة متكاملة شاملة مزودة بذكاء اصطناعي مدمج",
            "content": "تقدم Zoho مجموعة واسعة من التطبيقات التجارية، صُممت لتتكامل بسلاسة لتلبية احتياجات متنوعة"
          }
        ]
      },
      "step_view_content": {
        "main_header": {
          "Heading_One": "مصممة ",
          "Heading_Yellow": "للتوسع",
          "Heading_Third": "وللأداء العالي"
        },
        "step_content": [
          {
            "title": "#1 الاستكشاف",
            "content": "رؤى خبيرة وتخطيط استراتيجي مخصص لاحتياجات عملك بواسطة خبرائنا"
          },
          {
            "title": "#2 التصميم الأولي والنماذج",
            "content": "رسم هيكل الحل وتدفقه من خلال نماذج تفاعلية وتصاميم مبدئية"
          },
          {
            "title": "#3 التصميم",
            "content": "إنشاء تصاميم جذابة موجهة للمستخدم تعكس هوية علامتك بدقة عالية"
          },
          {
            "title": "#4 التطوير",
            "content": "بناء حلول ديناميكية ذات تأثير عالٍ تنمو معك – مدعومة بالابتكار الحديث"
          },
          {
            "title": "#5 اختبار القبول والنشر",
            "content": "اختبارات قبول دقيقة من قبل المستخدمين ونشر سلس بعد فحوصات جودة صارمة"
          }
        ]
      },
      "footerViewContent": {
        "getstart_card": {
          "header": "نمُ كالمحترفين، بدعم من زوهو!",
          "paragraph": "ركّز على احتياجاتك، واحصل على تطبيقات زوهو المثالية، ودعنا نُعزز اتصالك بالتقنية لأعمالك – مُصمم خصيصًا لك!",
          "cta_btn": "تواصل معنا!"
        },
        "footer_logo": {
          "paragraph": "بعيدًا جدًا، خلف جبال الكلمات، بعيدًا عن بلدان فوكاليا وكونسونانتيا، تعيش النصوص العمياء."
        },
        "footer_list": [
          {
            "title": "الروابط الداخلية",
            "Internal Links": [
              "المبيعات",
              "ما قبل البيع",
              "الشركاء",
              "التسويق",
              "الحسابات",
              "الإدارة"
            ],
          },
          {
            "title": "نبذة عن Zoho MEA",
            "About Zoho MEA": [
              "عام",
              "الاتصالات",
              "المشاريع"
            ]
          },
          {
            "title": "اتصل بالمبيعات",
            "Contact Sales": [
              {
                "Phone": [
                  "1800 103 1123",
                  "1800 572 3535"
                ]
              },
              {
                "Email": [
                  "sales@zohocorp.com"
                ]
              }
            ]
          }
        ],

      }

    }

  };
  const i18n = createI18n({
    legacy: false,
    locale: 'en',
    fallbackLocale: 'en',
    messages: homeHtmlContent,
  });
  store.dispatch('updateEnvironment');
  app.use(store);
  app.use(i18n);
  app.mount('#app');
  app.config.globalProperties.$i18n = i18n.global;

  // Mounting is synchronous, so if the first view needs to load data its
  // <Loader> has already taken a hold by now. Releasing the boot hold there-
  // fore hands over without the count reaching zero, and the overlay stays up
  // on the same uninterrupted animation until the view is actually ready.
  loader.booted();
  loader.release();

  // Registered after mount so the initial navigation (already resolved by
  // router.isReady above) doesn't release a hold it never took. Covers the
  // blank gap while a lazy-loaded route chunk is being fetched.
  let navHold = false;
  const holdNav = () => { if (!navHold) { navHold = true; loader.acquire(); } };
  const dropNav = () => { if (navHold) { navHold = false; loader.release(); } };

  router.beforeEach((to, from, next) => {
    // The boot-time redirect only covers the entry URL. This covers navigation
    // inside the app — the header's Login button, or a stale link — so /login
    // stays unreachable for as long as the switch is on. isLoginRemoved() is
    // safe to read synchronously here: the flag is awaited before mount.
    if (isLoginRemoved() && (to.path === '/login' || to.path === '/training/login')) {
      return next({ path: '/', replace: true });
    }

    if (from.name) holdNav();
    next();
  });

  // Released on nextTick, not synchronously in afterEach.
  //
  // afterEach fires when the navigation is confirmed, which is BEFORE the
  // incoming view has rendered and its own <Loader> has taken a hold. Dropping
  // the nav hold there let the count reach zero in between, so the overlay was
  // torn down and then immediately rebuilt by the new view — the loader
  // appearing to run twice, which is what the lag on the Solutions tab was.
  //
  // nextTick lands after the render flush, so the handoff overlaps and the
  // count never touches zero. The 60ms hide debounce in index.html is a second
  // line of defence for a view that mounts a frame later.
  router.afterEach(() => { nextTick(dropNav); });
  router.onError(dropNav);
}

initApp().catch((e) => {
  console.error('App bootstrap failed:', e);
  loader.booted();
  loader.release();
})